

export enum UserRole {
  Admin = 'ADMIN',
  Staff = 'STAFF',
  Client = 'CLIENT',
}

export enum OrderStatus {
  Received = 'Received',
  PriceChecking = 'PriceChecking',
  Quoted = 'Quoted',
  Confirmed = 'Confirmed',
  Purchased = 'Purchased',
  Shipped = 'Shipped',
  Delivered = 'Delivered',
  Cancelled = 'Cancelled'
}

export enum PaymentStatus {
  Pending = 'Pending',
  Paid = 'Paid',
  Verified = 'Verified',
}

export enum ShippingType {
  Sea = 'Sea',
  Air = 'Air',
}

export enum OrderType {
  Sourcing = 'Sourcing',
  Shipping = 'Shipping',
  General = 'General',
  Incoming = 'Incoming',
}

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  password?: string;
  teCode?: string; // Tag Express customer code
  isVerified?: boolean; // Admin approval
  isLocked?: boolean;
  countryCode?: string;
  phone?: string;
  city?: string;
  isEmailVerified?: boolean;
}

export interface Quote {
  id: string;
  staffId: string;
  supplier: string;
  price: number;
  shippingCost: number;
  deliveryTime: string;
  invoiceUrl?: string;
  createdAt: string;
}

export interface StatusHistory {
  status: OrderStatus;
  timestamp: string;
}

export interface ChatMessage {
  id: string;
  senderId: string;
  text: string;
  imageUrl?: string;
  createdAt: string;
}

export interface Notification {
  id: string;
  userId: string; // The user who receives the notification
  senderId?: string; // The user who triggered the notification (or system)
  message: string; // Fallback for old notifications or simple messages
  messageKey?: string; // e.g., 'notification_new_order'
  messageParams?: { [key: string]: string | number }; // e.g., { orderId: 'TX-123' }
  link: string; // e.g., `/orders/order-1`
  isRead: boolean;
  createdAt: string;
}

export interface PriceCheckRequest {
  staffId: string;
  status: 'pending' | 'submitted';
  price?: number;
  shippingCost?: number;
  deliveryTime?: string;
  notes?: string;
  submittedAt?: string;
}

export interface Order {
  id: string;
  orderId: string; // e.g., TX-2025-0001
  clientId: string;
  productName: string;
  productLink?: string;
  productImageUrl?: string;
  quantity: number;
  notes?: string;
  status: OrderStatus;
  paymentStatus: PaymentStatus;
  createdAt: string;
  updatedAt: string;
  quotes: Quote[];
  finalPrice?: number;
  commission?: number;
  trackingNumber?: string;
  carrier?: string;
  shippingType?: ShippingType;
  statusHistory?: StatusHistory[];
  // New fields for different order types
  orderType: OrderType;
  isQuoteEnabled?: boolean;
  weight?: number; // in kg for shipping orders
  dimensions?: string; // e.g., "50x40x30 cm" for shipping orders
  messages?: ChatMessage[];
  assignedStaffId?: string;
  sourceWarehouseId?: string; // ID of the warehouse for shipping orders
  priceCheckRequests?: PriceCheckRequest[];
  priceCheckStaffIds?: { [key: string]: true }; // Switched to object for efficient querying

  // Fields for IncomingOrdersPage
  priority?: 'Urgent' | 'High' | 'Medium' | 'Low';
  estimatedArrival?: string; // ISO date string
  source?: string; // e.g. 'Shanghai'
  destination?: string; // e.g. 'Sulaymaniyah'
  deliveryTime?: string; // Estimated delivery time from quote
}

export interface Warehouse {
  id: string;
  name: string;
  country: string;
  address: string;
}

export interface NotificationPreference {
  id: string;
  name: string;
  description: string;
  emailEnabled: boolean;
  smsEnabled: boolean;
}